<template>
    <div class="container mx-auto my-20 px-4 max-w-6xl">
        <div class="grid md:grid-cols-5 gap-6 bg-gray-50 p-4 rounded-lg">
            <div class="md:col-span-3">
                <div class="flex justify-between items-center text-gray-600">
                    <button class="focus:outline-none"
                            :class="{ 'invisible': hidePreviousMonthArrow }"
                            @click="previousMonth()"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <span>{{ selectedMonth }}</span>

                    <button class="focus:outline-none"
                            @click="nextMonth()"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>

                <div class="grid grid-cols-7 gap-1 mt-4">
                    <div class="text-gray-500 text-center pb-2">Mo</div>
                    <div class="text-gray-500 text-center pb-2">Tu</div>
                    <div class="text-gray-500 text-center pb-2">We</div>
                    <div class="text-gray-500 text-center pb-2">Th</div>
                    <div class="text-gray-500 text-center pb-2">Fr</div>
                    <div class="text-gray-500 text-center pb-2">Sa</div>
                    <div class="text-gray-500 text-center pb-2">Su</div>

                    <button class="relative ratio-1:1 rounded text-gray-500 focus:outline-none transition-colors duration-300 ease-in-out"
                            :class="{
                                'bg-gray-100': day.isPast,
                                'bg-gray-200': ! day.isPadding && ! day.isPast && ! day.isToday,
                                'bg-primary-200': day.isToday,
                                'bg-primary-600 text-primary-200': day === selectedDay,
                                'cursor-default': day.isPadding,
                            }"
                            v-for="day in days"
                            @click="selectDay(day)"
                    >
                        <span class="absolute inset-0 pointer-events-none sm:pt-2 px-1 sm:px-3 text-right text-sm sm:text-base">
                            {{ day.date }}
                        </span>

                        <span class="absolute inset-0 pointer-events-none pb-2">
                            <span class="h-full flex items-end justify-center space-x-0.5 sm:space-x-1">
                                <span class="block w-2 sm:w-3 h-2 sm:h-3 rounded-full"
                                      :class="event.colour"
                                      v-for="event in events[day.date]"
                                ></span>
                            </span>
                        </span>
                    </button>
                </div>
            </div>

            <div class="md:col-span-2">
                <div class="flex flex-col justify-between h-full">
                    <div>
                        <div class="flex justify-between items-center">
                            <h3 class="mb-2">Events</h3>
                        </div>

                        <template v-for="date in filteredEvents">
                            <a :href="`/${event.url}`"
                               class="block hover:bg-gray-100 p-2 rounded"
                               v-for="event in date"
                               v-if="! hideFullyBooked || event.seats > 0"
                            >
                                <div class="flex justify-between items-center">
                                    <div class="leading-6">
                                        <div class="flex items-start">
                                            <div class="w-3 h-3 rounded-full flex-shrink-0 mt-1.5"
                                                 :class="event.colour"
                                            ></div>
                                            <div class="ml-2 text-lg text-gray-700 font-medium">{{ event.name }}</div>
                                        </div>

                                        <div class="text-gray-500 pl-5" v-html="event.date"></div>
                                    </div>

                                    <div class="text-sm text-gray-500 text-right">
                                        <div class="font-semibold">{{ event.type }}</div>
                                        <div v-if="event.seats > 0">
                                            {{ event.seats }} Seat{{ event.seats !== 1 ? 's' : '' }} Available
                                        </div>
                                        <div v-else>Fully Booked</div>
                                    </div>
                                </div>
                            </a>
                        </template>
                    </div>

                    <div class="flex justify-between items-center mt-6">
                        <div class="flex items-center">
                            <form-toggle v-model="hideFullyBooked"
                                         class="mr-1"
                            ></form-toggle>
                            <span class="text-gray-600">Hide fully booked events</span>
                        </div>

                        <div class="relative group">
                            <div class="text-sm text-gray-500 font-semibold cursor-default">Key</div>

                            <div class="absolute pointer-events-none bottom-0 right-full z-10 bg-gray-800 px-3 py-2 mr-1 rounded opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100">
                                <div class="flex items-center"
                                     v-for="type in types"
                                >
                                    <div class="w-3 h-3 rounded-full flex-shrink-0"
                                         :class="type.calendar_colour"
                                    ></div>
                                    <div class="ml-2 text-white font-medium whitespace-no-wrap">{{ type.name }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { startOfMonth, endOfMonth, getDay, getDaysInMonth, addMonths, subMonths, format, isSameMonth, getYear, getMonth, isPast, isToday } from 'date-fns';
    import FormToggle from "./form/Toggle";

    export default {
        data() {
            return {
                days: [],
                events: [],
                eventList: [],
                currentMonth: null,
                selectedDate: null,
                daysInMonth: null,
                daysWithOffets: null,
                offsets: {
                    start: null,
                    end: null,
                },

                selectedDay: null,

                isLoading: false,

                hideFullyBooked: false,
            }
        },

        props: {
            types: {
                type: Array,
            }
        },

        components: { FormToggle },

        mounted() {
            this.initialise();
        },

        computed: {
            selectedMonth() {
                return this.selectedDate ? format(this.selectedDate, 'MMMM yyyy') : '';
            },

            isCurrentMonth() {
                return this.currentMonth ? isSameMonth(this.currentMonth, this.selectedDate) : true;
            },

            filteredEvents() {
                if (_.isNull(this.selectedDay)) return this.events;

                return [this.events[this.selectedDay.date]];
            },

            hidePreviousMonthArrow() {
                let now = new Date;

                return getYear(this.selectedDate) === getYear(now)
                    && getMonth(this.selectedDate) === getMonth(now);
            }
        },

        methods: {
            initialise() {
                this.currentMonth = startOfMonth(new Date());
                this.selectedDate = this.currentMonth;
                this.setMonth();
            },

            createDays() {
                let days = [];

                for (let i = 1; i <= this.daysWithOffets; i++) {
                    let day = { date: '', isPadding: true, isPast: false, isToday: false };

                    if (i > this.offsets.start && i <= this.daysInMonth + this.offsets.start) {
                        day.date = i - this.offsets.start;
                        day.isPadding = false;
                        day.isPast = isPast(new Date(getYear(this.selectedDate), getMonth(this.selectedDate), i - this.offsets.start + 1));
                        day.isToday = isToday(new Date(getYear(this.selectedDate), getMonth(this.selectedDate), i  - this.offsets.start))
                    }

                    days.push(day);
                }

                this.days = days;
            },

            setMonth() {
                this.daysInMonth = getDaysInMonth(this.selectedDate);
                this.offsets.start = getDay(startOfMonth(this.selectedDate)) - 1;

                if (this.offsets.start < 0) {
                    this.offsets.start = 6;
                }

                this.offsets.end = 7 - getDay(endOfMonth(this.selectedDate));
                this.daysWithOffets = this.daysInMonth + this.offsets.start + this.offsets.end;
                this.createDays();
                this.getEvents();
            },

            getEvents() {
                this.isLoading = true;
                this.events = [];

                axios.post('events-calendar', {
                    year: getYear(this.selectedDate),
                    month: getMonth(this.selectedDate) + 1,
                }).then(response => {
                    this.events = response.data;
                    this.isLoading = false;
                })
            },

            previousMonth() {
                if (this.isCurrentMonth || this.isLoading) return;

                this.selectedDate = subMonths(this.selectedDate, 1);
                this.selectedDay = null;
                this.setMonth();
            },

            nextMonth() {
                if (this.isLoading) return;

                this.selectedDate = addMonths(this.selectedDate, 1);
                this.selectedDay = null;
                this.setMonth();
            },

            selectDay(day) {
                if (day.isPast || day.isPadding) return;

                this.selectedDay = day === this.selectedDay ? null : day;
            },
        }
    }
</script>
